import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { Descriptions, Radio, RadioChangeEvent, Spin } from 'antd';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Button from '../../components/Button';
import { formatAmount, getVat, removeComma } from '../../lib/util';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SelectCampus from '../../components/SelectCampus';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import { updateCampusSetting } from '../../api/campuses.api';
import ErrorResponse from '../../components/ErrorResponse';
import useNotification from '../../hooks/useNotification';
import { useGetCampusSettingQuery } from '../../api/shuttle/shuttle.api';

const CampusSetting = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { successNotification } = useNotification();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [shuttleAmountYn, setShuttleAmountYn] = useState('N');
  const [shuttleAmount, setShuttleAmount] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [platAmount, setPlatAmount] = useState('');
  const [accuracyClinicYn, setAccuracyClinicYn] = useState('N');
  const [guestPlatRsvtYn, setGuestPlatRsvtYn] = useState('N');
  const [confirmSubmitVisible, setConfirmSubmitVisible] = useState(false);
  const TAX_TYPE = {
    FREE: '0'
  }

  const {
    data: campuses,
    isLoading: isGetCampusesLoading,
    refetch: getCampusesRefetch,
  } = useGetCampusSettingQuery(currentCampus, { skip: !currentCampus });

  const _campuses = campuses?.data;

  useEffect(() => {
    if (!campuses)
      return;

    setShuttleAmountYn(_campuses?.shuttleAmountYn || 'N');
    setShuttleAmount(
      _campuses?.shuttleAmount
        ? formatAmount(String(Number(_campuses?.shuttleAmount)))
        : '',
    );
    setListPrice(
      _campuses?.listPrice
        ? formatAmount(String(Number(_campuses?.listPrice)))
        : '',
    );
    setPlatAmount(
      _campuses?.platAmount
        ? formatAmount(String(Number(_campuses?.platAmount)))
        : '',
    );
    setAccuracyClinicYn(_campuses?.accuracyClinicYn || 'N');
    setGuestPlatRsvtYn(_campuses?.guestPlatRsvtYn || 'N');
  }, [campuses]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      setConfirmSubmitVisible(false);
      successNotification(t('common.message.save1'));
      getCampusesRefetch(currentCampus);
    }
  }, [submitRes, submitIsLoading]);

  const onChangeShuttleAmountYn = (e: RadioChangeEvent) => {
    setShuttleAmountYn(e.target.value);
  };

  const onChangeSetAccuracyClinicYn = (e: RadioChangeEvent) => {
    setAccuracyClinicYn(e.target.value);
  };

  const onChangeGuestPlatRsvtYn = (e: RadioChangeEvent) => {
    setGuestPlatRsvtYn(e.target.value);
  };

  const onSubmit = async () => {
    const req = {
      shuttleAmountYn,
      shuttleAmount: removeComma(shuttleAmount),
      shuttleTaxAmt: TAX_TYPE.FREE,
      listPrice: removeComma(listPrice),
      platAmount: removeComma(platAmount),
      platTaxAmt: getAmount(platAmount, 'vat'),
      accuracyClinicYn,
      guestPlatRsvtYn,
    };

    submitExecute(updateCampusSetting(currentCampus, req));
  };

  const getAmount = (n: string, type: string) => {
    if (!n) return '';

    const amount = parseInt(removeComma(n));
    const vat = getVat(amount);
    return type === 'vat'
      ? formatAmount(String(vat))
      : formatAmount(String(amount - vat));
  };

  return (
    <>
    <div className="content scrollbar">
      <ErrorResponse error={submitError} />
      <SimpleHeader
        iconFile="icon-campus-big.png"
        parentMenu={t('common.menu.campus')}
        currentMenu={t('campus.setting.title1')}
        onlyTitle={true}
      />

      <div className="comm-table__area rowType">
        <Spin spinning={isGetCampusesLoading}>
          <Descriptions
            bordered
            size="small"
            column={1}
            labelStyle={{ width: '25%' }}
          >
            <Descriptions.Item label={t('common.menu.campus')}>
              <div className="w-150p">
                <SelectCampus />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('campus.setting.column1')}>
              <div className='auth-radio__area'>
                <Radio.Group onChange={onChangeShuttleAmountYn} value={shuttleAmountYn}>
                  <Radio value="Y">{t('common.option.yes')}</Radio>
                  <Radio value="N">{t('common.option.no')}</Radio>
                </Radio.Group>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('campus.setting.column2')}>
              <div className="flex">
                  <span className="v-line ml-1">
                    {t('campus.setting.text1')}
                  </span>
                <input
                  type="text"
                  className="form-control input-text w-100p"
                  value={shuttleAmount}
                  onChange={e => {
                    setShuttleAmount(formatAmount(e.target.value));
                  }}
                  disabled={shuttleAmountYn === 'N'}
                />
                <span className="v-line ml-1">
                    {t('campus.setting.text2')}{' '}
                  {
                    shuttleAmount
                      ? `(${t('campus.setting.duty-free-price')}: ${shuttleAmount}${t('campus.setting.text2')})`
                      : null
                  }
                  </span>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('campus.setting.column3')}>
              <div>
                <div className="flex mb-1">
                  <span className="v-line mr-1">{t('campus.setting.text5')}</span>
                  <input
                    type="text"
                    className="form-control input-text w-100p"
                    value={listPrice}
                    onChange={e => {
                      setListPrice(formatAmount(e.target.value));
                    }}
                  />
                  <span className="v-line ml-1">{t('campus.setting.text2')}</span>
                </div>
                <div className="flex">
                  <span className="v-line mr-1">{t('campus.setting.text6')}</span>
                  <input
                    type="text"
                    className="form-control input-text w-100p"
                    value={platAmount}
                    onChange={e => {
                      setPlatAmount(formatAmount(e.target.value));
                    }}
                  />
                  <span className="v-line ml-1">
                      {t('campus.setting.text2')}{' '}
                    {
                      platAmount
                        ? `(${t('campus.setting.text3')}: ${getAmount(platAmount, 'supplied')}${t('campus.setting.text2')}, ${t('campus.setting.text4')}: ${getAmount(platAmount, 'vat')}${t('campus.setting.text2')})`
                        : null
                    }
                    </span>
                </div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('campus.setting.column4')}>
              <div className='auth-radio__area'>
                <Radio.Group onChange={onChangeSetAccuracyClinicYn} value={accuracyClinicYn}>
                  <Radio value="Y">{t('common.option.yes')}</Radio>
                  <Radio value="N">{t('common.option.no')}</Radio>
                </Radio.Group>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('간편 입학시험 예약 활성화 여부')}>  {/* campus.setting.column5 */}
              <div className='auth-radio__area'>
                <Radio.Group onChange={onChangeGuestPlatRsvtYn} value={guestPlatRsvtYn}>
                  <Radio value="Y">{t('common.option.yes')}</Radio>
                  <Radio value="N">{t('common.option.no')}</Radio>
                </Radio.Group>
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Spin>

        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            <Button
              onClick={() => setConfirmSubmitVisible(true)}
              className="btn-basic pink"
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
              disabled={isGetCampusesLoading}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        visible={confirmSubmitVisible}
        setVisible={setConfirmSubmitVisible}
        onConfirm={onSubmit}
        titleText={t('campus.setting.title2')}
        messageText={t('campus.setting.text7')}
        confirmText={t('common.button.save')}
        cancelText={t('common.button.cancel')}
      />
    </div>
    </>
  );
};

export default React.memo(CampusSetting);
